import React, { useMemo, useState } from "react";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import toast from "react-hot-toast";
import {
  useGetSupportContactReasonsQuery,
  useGetSupportContactsQuery,
  useMarkSupportContactReadMutation,
} from "../../store/services/support";
import { SupportContactItem, SupportContactsQueryParams } from "../../models/support";
import { formatSupportDateTimeMinutes } from "../../utils/functions/parseInstant";

const getApiError = (e: any): string => e?.data?.error?.message || "Ocurrió un error";

type ReadFilter = "all" | "read" | "unread";

const readOptions: { label: string; value: ReadFilter }[] = [
  { label: "Todos", value: "all" },
  { label: "No leídos", value: "unread" },
  { label: "Leídos", value: "read" },
];

export function SupportContacts() {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [reasonId, setReasonId] = useState<string | null>(null);
  const [readFilter, setReadFilter] = useState<ReadFilter>("all");
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [selectedContact, setSelectedContact] = useState<SupportContactItem>();

  const { data: reasons, isLoading: isLoadingReasons } = useGetSupportContactReasonsQuery();
  const [markRead, { isLoading: isMarkingRead }] = useMarkSupportContactReadMutation();

  const queryParams: SupportContactsQueryParams = useMemo(
    () => ({
      page,
      limit,
      ...(reasonId ? { reasonId } : {}),
      ...(readFilter === "read"
        ? { isRead: true }
        : readFilter === "unread"
        ? { isRead: false }
        : {}),
      ...(dateFrom ? { dateFrom: dateFrom.toISOString() } : {}),
      ...(dateTo ? { dateTo: dateTo.toISOString() } : {}),
    }),
    [page, limit, reasonId, readFilter, dateFrom, dateTo]
  );

  const { data: contactsResult, isLoading, isFetching } =
    useGetSupportContactsQuery(queryParams);

  const reasonOptions = useMemo(
    () => (reasons ?? []).map((r) => ({ label: r.label, value: r.id })),
    [reasons]
  );

  const resetPage = () => setPage(1);

  const handlePage = (e: DataTablePageEvent) => {
    setPage((e.page ?? 0) + 1);
    setLimit(e.rows);
  };

  const handleMarkRead = () => {
    if (!selectedContact) return;
    markRead(selectedContact.id)
      .unwrap()
      .then(() => {
        toast.success("Contacto marcado como leído");
        setSelectedContact({ ...selectedContact, readAt: new Date().toISOString() });
      })
      .catch((error) => {
        toast.error(getApiError(error));
      });
  };

  const dateBodyTemplate = (row: SupportContactItem) =>
    formatSupportDateTimeMinutes(row.createdAt);

  const emailSentBodyTemplate = (row: SupportContactItem) =>
    row.emailSent ? (
      <Tag severity="success" value="Sí" />
    ) : (
      <Tag severity="secondary" value="No" />
    );

  const statusBodyTemplate = (row: SupportContactItem) =>
    row.readAt ? (
      <Tag severity="success" value="Leído" />
    ) : (
      <Tag severity="warning" value="No leído" />
    );

  const actionsBodyTemplate = (row: SupportContactItem) => (
    <Button
      icon="pi pi-eye"
      rounded
      outlined
      tooltip="Ver detalle"
      tooltipOptions={{ position: "top" }}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedContact(row);
      }}
    />
  );

  const detailFooter = selectedContact && !selectedContact.readAt && (
    <div className="flex justify-content-end">
      <Button
        label="Marcar como leído"
        icon="pi pi-check"
        onClick={handleMarkRead}
        loading={isMarkingRead}
      />
    </div>
  );

  return (
    <>
      <div className="p-card gap-4 p-3">
        <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-6">
          <h1 className="text-center m-0">Contactos de soporte</h1>
        </div>

        <div className="flex flex-wrap align-items-end gap-3 mb-4">
          <div className="flex flex-column gap-2">
            <label htmlFor="reasonFilter" className="text-600 text-sm">
              Motivo
            </label>
            <Dropdown
              id="reasonFilter"
              value={reasonId}
              options={reasonOptions}
              optionLabel="label"
              optionValue="value"
              loading={isLoadingReasons}
              onChange={(e) => {
                setReasonId(e.value ?? null);
                resetPage();
              }}
              placeholder="Todos los motivos"
              showClear
              style={{ minWidth: "14rem" }}
            />
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="readFilter" className="text-600 text-sm">
              Estado
            </label>
            <Dropdown
              id="readFilter"
              value={readFilter}
              options={readOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => {
                setReadFilter(e.value);
                resetPage();
              }}
              style={{ minWidth: "10rem" }}
            />
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="dateFrom" className="text-600 text-sm">
              Desde
            </label>
            <Calendar
              id="dateFrom"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom((e.value as Date | null) ?? null);
                resetPage();
              }}
              dateFormat="dd/mm/yy"
              placeholder="Fecha desde"
              showButtonBar
              maxDate={dateTo ?? undefined}
            />
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="dateTo" className="text-600 text-sm">
              Hasta
            </label>
            <Calendar
              id="dateTo"
              value={dateTo}
              onChange={(e) => {
                setDateTo((e.value as Date | null) ?? null);
                resetPage();
              }}
              dateFormat="dd/mm/yy"
              placeholder="Fecha hasta"
              showButtonBar
              minDate={dateFrom ?? undefined}
            />
          </div>
        </div>

        <DataTable
          value={contactsResult?.items ?? []}
          loading={isLoading || isFetching}
          lazy
          paginator
          first={(page - 1) * limit}
          rows={limit}
          totalRecords={contactsResult?.total ?? 0}
          rowsPerPageOptions={[5, 10, 25, 50]}
          onPage={handlePage}
          tableStyle={{ minWidth: "70rem" }}
          responsiveLayout="scroll"
          emptyMessage="No hay contactos para mostrar."
          selectionMode="single"
          onRowClick={(e) => setSelectedContact(e.data as SupportContactItem)}
        >
          <Column header="Fecha" body={dateBodyTemplate} style={{ width: "11rem" }} />
          <Column
            header="Usuario"
            body={(row: SupportContactItem) => row.userName || `Usuario #${row.userId}`}
            style={{ width: "12rem" }}
          />
          <Column
            header="Email"
            body={(row: SupportContactItem) => row.userEmail || "—"}
            style={{ width: "14rem" }}
          />
          <Column
            header="Motivo"
            body={(row: SupportContactItem) => row.reasonLabel || "—"}
            style={{ width: "12rem" }}
          />
          <Column field="messagePreview" header="Mensaje" style={{ minWidth: "18rem" }} />
          <Column header="Email enviado" body={emailSentBodyTemplate} style={{ width: "9rem" }} />
          <Column header="Estado" body={statusBodyTemplate} style={{ width: "8rem" }} />
          <Column header="Acciones" body={actionsBodyTemplate} style={{ width: "6rem" }} />
        </DataTable>
      </div>

      <Dialog
        visible={!!selectedContact}
        header="Detalle del contacto"
        modal
        style={{ width: "36rem", maxWidth: "95vw", maxHeight: "90vh" }}
        contentStyle={{ overflowY: "auto" }}
        footer={detailFooter}
        onHide={() => setSelectedContact(undefined)}
      >
        {selectedContact && (
          <div className="flex flex-column gap-3">
            <div className="flex flex-wrap gap-4">
              <div>
                <small className="text-600 block">Fecha</small>
                <span className="font-medium">{dateBodyTemplate(selectedContact)}</span>
              </div>
              <div>
                <small className="text-600 block">Usuario</small>
                <span
                  className="font-medium"
                  style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                >
                  {selectedContact.userName || `Usuario #${selectedContact.userId}`}
                </span>
              </div>
              <div>
                <small className="text-600 block">Email</small>
                <span
                  className="font-medium"
                  style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                >
                  {selectedContact.userEmail || "—"}
                </span>
              </div>
              <div>
                <small className="text-600 block">Motivo</small>
                <span
                  className="font-medium"
                  style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                >
                  {selectedContact.reasonLabel || "—"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <div>
                <small className="text-600 block">Email enviado</small>
                {emailSentBodyTemplate(selectedContact)}
              </div>
              <div>
                <small className="text-600 block">Estado</small>
                {statusBodyTemplate(selectedContact)}
              </div>
            </div>

            <div>
              <small className="text-600 block mb-2">Mensaje</small>
              <div
                className="p-3 border-1 surface-border border-round surface-ground"
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  maxHeight: "22rem",
                  overflow: "auto",
                }}
              >
                {selectedContact.message}
              </div>
            </div>

            {selectedContact.attachmentUrl && (
              <div>
                <small className="text-600 block mb-1">Adjunto</small>
                <a
                  href={selectedContact.attachmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-medium"
                  style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                >
                  <i className="pi pi-paperclip mr-1"></i>
                  Ver adjunto
                </a>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
}
